import { useState, useCallback } from 'react';
import { message } from 'antd';
import { t20aApi, CFT160, CFT161, CFT162, CFT163, CFT164 } from '../../../services/t20aApi';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useT20AMode';

export function useT20ARecord() {
  const [record, setRecord] = useState<CFT160 | null>(null);
  const [formData, setFormData] = useState<Partial<CFT160>>({});
  const [attendees, setAttendees] = useState<Partial<CFT161>[]>([]);
  const [agendas, setAgendas] = useState<Partial<CFT162>[]>([]);
  const [customerPeople, setCustomerPeople] = useState<Partial<CFT164>[]>([]);
  const [loading, setLoading] = useState(false);
  const { clearFieldErrors, getFieldError, setFieldErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{ data: CFT160 } | null> => {
    setLoading(true);
    try {
      const data = await t20aApi.get(id);
      setRecord(data);
      setFormData({ ...data });
      setAttendees(data.attendees || []);
      setAgendas(data.agendas || []);
      setCustomerPeople(data.customer_people || []);
      return { data };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof CFT160, value: CFT160[keyof CFT160]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof CFT160): CFT160[keyof CFT160] => {
    return (formData as Record<string, CFT160[keyof CFT160]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: CFT160) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as CFT160,
        attendees: attendees as CFT161[],
        agendas: agendas as CFT162[],
        customer_people: customerPeople as CFT164[],
      };
      if (mode === 'add') {
        const result = await t20aApi.create(req);
        message.success(`新增成功: ${result.ft160_01}`);
        onSuccess(result);
      } else {
        const result = await t20aApi.update(formData.ft160_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setAttendees(result.attendees || []);
        setAgendas(result.agendas || []);
        setCustomerPeople(result.customer_people || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map(er => er.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, attendees, agendas, customerPeople, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await t20aApi.delete(record.ft160_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  // --- Attendees (CFT161) ---
  const addAttendeeRow = useCallback(() => {
    const maxSeq = attendees.reduce((max, d) => Math.max(max, d.ft161_02 || 0), 0);
    setAttendees(prev => [...prev, { ft161_02: maxSeq + 1, ft161_01: formData.ft160_01 || '' }]);
    setFormData(prev => ({ ...prev, ft160_18: (prev.ft160_18 || 0) + 1 }));
  }, [attendees, formData.ft160_01]);

  const deleteAttendeeRow = useCallback((idx: number) => {
    setAttendees(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((d, i) => ({ ...d, ft161_02: i + 1 })); // T-F05 reindex
    });
    setFormData(prev => ({ ...prev, ft160_18: Math.max(0, (prev.ft160_18 || 0) - 1) }));
  }, []);

  const setAttendeeField = useCallback((idx: number, key: keyof CFT161, value: CFT161[keyof CFT161]) => {
    setAttendees(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, []);

  // --- Agendas (CFT162) ---
  const addAgendaRow = useCallback(() => {
    const maxSeq = agendas.reduce((max, d) => Math.max(max, d.ft162_02 || 0), 0);
    setAgendas(prev => [...prev, { ft162_02: maxSeq + 1, ft162_01: formData.ft160_01 || '' }]);
    setFormData(prev => ({ ...prev, ft160_19: (prev.ft160_19 || 0) + 1 }));
  }, [agendas, formData.ft160_01]);

  const deleteAgendaRow = useCallback((idx: number) => {
    setAgendas(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((d, i) => ({ ...d, ft162_02: i + 1 })); // T-F05 reindex
    });
    setFormData(prev => ({ ...prev, ft160_19: Math.max(0, (prev.ft160_19 || 0) - 1) }));
  }, []);

  const setAgendaField = useCallback((idx: number, key: keyof CFT162, value: CFT162[keyof CFT162]) => {
    setAgendas(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, []);

  // --- Tracking Items (CFT163) — nested under agendas ---
  const addTrackingItem = useCallback((agendaIdx: number) => {
    setAgendas(prev => {
      const next = [...prev];
      const agenda = next[agendaIdx];
      if (!agenda) return prev;
      const items = agenda.tracking_items || [];
      const maxSeq = items.reduce((max, d) => Math.max(max, d.ft163_03 || 0), 0);
      next[agendaIdx] = {
        ...agenda,
        tracking_items: [...items, {
          ft163_01: formData.ft160_01 || '',
          ft163_02: agenda.ft162_02 || 0,
          ft163_03: maxSeq + 1,
          ft163_11: '',
          ft163_12: null,
          ft163_13: '',
          ft163_14: null,
          ft163_15: '',
          ft163_15a: '',
          ft163_16: '',
          ft163_16a: '',
        }],
      };
      return next;
    });
  }, [formData.ft160_01]);

  const deleteTrackingItem = useCallback((agendaIdx: number, trackIdx: number) => {
    setAgendas(prev => {
      const next = [...prev];
      const agenda = next[agendaIdx];
      if (!agenda) return prev;
      const items = (agenda.tracking_items || []).filter((_, i) => i !== trackIdx);
      next[agendaIdx] = {
        ...agenda,
        tracking_items: items.map((d, i) => ({ ...d, ft163_03: i + 1 })), // T-F05 reindex
      };
      return next;
    });
  }, []);

  const setTrackingField = useCallback((agendaIdx: number, trackIdx: number, key: keyof CFT163, value: CFT163[keyof CFT163]) => {
    setAgendas(prev => {
      const next = [...prev];
      const agenda = next[agendaIdx];
      if (!agenda) return prev;
      const items = [...(agenda.tracking_items || [])];
      if (items[trackIdx]) {
        items[trackIdx] = { ...items[trackIdx], [key]: value };
      }
      next[agendaIdx] = { ...agenda, tracking_items: items };
      return next;
    });
  }, []);

  // --- Customer People (CFT164) ---
  const addCustomerRow = useCallback(() => {
    const maxSeq = customerPeople.reduce((max, d) => Math.max(max, d.ft164_02 || 0), 0);
    setCustomerPeople(prev => [...prev, { ft164_02: maxSeq + 1, ft164_01: formData.ft160_01 || '' }]);
    setFormData(prev => ({ ...prev, ft160_25: (prev.ft160_25 || 0) + 1 }));
  }, [customerPeople, formData.ft160_01]);

  const deleteCustomerRow = useCallback((idx: number) => {
    setCustomerPeople(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((d, i) => ({ ...d, ft164_02: i + 1 })); // T-F05 reindex
    });
    setFormData(prev => ({ ...prev, ft160_25: Math.max(0, (prev.ft160_25 || 0) - 1) }));
  }, []);

  const setCustomerField = useCallback((idx: number, key: keyof CFT164, value: CFT164[keyof CFT164]) => {
    setCustomerPeople(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, []);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setAttendees(record.attendees || []);
      setAgendas(record.agendas || []);
      setCustomerPeople(record.customer_people || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    const today = new Date().toISOString().substring(0, 10) + 'T00:00:00Z';
    setFormData({
      ft160_13: today,
      ft160_18: 0,
      ft160_19: 0,
      ft160_25: 0,
    });
    setAttendees([]);
    setAgendas([]);
    setCustomerPeople([]);
  }, [clearFieldErrors]);

  /** Apply result after save (T-F01: use mutation response directly) */
  const applyResult = useCallback((result: CFT160) => {
    setRecord(result);
    setFormData({ ...result });
    setAttendees(result.attendees || []);
    setAgendas(result.agendas || []);
    setCustomerPeople(result.customer_people || []);
  }, []);

  return {
    record, formData, attendees, agendas, customerPeople, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    // Attendees
    setAttendees, addAttendeeRow, deleteAttendeeRow, setAttendeeField,
    // Agendas
    setAgendas, addAgendaRow, deleteAgendaRow, setAgendaField,
    // Tracking Items
    addTrackingItem, deleteTrackingItem, setTrackingField,
    // Customer People
    setCustomerPeople, addCustomerRow, deleteCustomerRow, setCustomerField,
    // Mode helpers
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  };
}
